// СУП.1: модалка саппорта внутри Mini App.
//
// Три view внутри одной модалки:
//   1. list   — список тикетов игрока + кнопка «Новый запрос».
//   2. create — выбор темы + поле первого сообщения + «Отправить».
//   3. chat   — переписка с саппортом по тикету (read-only если closed).

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTelegram } from '../telegram/useTelegram';
import { useToast } from './Toast';
import { useTr } from '../i18n/useTr';
import {
  getSupportTopics, listSupportTickets, getSupportTicket,
  createSupportTicket, postSupportMessage,
  type SupportTopicDto, type SupportTopic, type SupportTicketSummary,
  type SupportTicketDetail
} from '../api/client';

type View = 'list' | 'create' | 'chat';

interface Props {
  onClose: () => void;
}

function buildStatusLabel(t: (ru: string, en: string) => string): Record<string, { txt: string; color: string }> {
  return {
    open:         { txt: t('Открыт', 'Open'),         color: 'var(--neon-cy)' },
    in_progress:  { txt: t('В работе', 'In progress'), color: 'var(--neon-am)' },
    in_waiting:   { txt: t('Ждёт тебя', 'Waiting for you'), color: 'var(--neon-mg)' },
    closed:       { txt: t('Закрыт', 'Closed'),       color: 'var(--t-md)' }
  };
}

export function SupportModal({ onClose }: Props) {
  const { hapticTap, hapticSuccess, hapticError } = useTelegram();
  const toast = useToast(s => s.show);
  const tr = useTr();
  const isEn = tr('ru', 'en') === 'en';
  const STATUS_LABEL = buildStatusLabel(tr);
  const [view, setView] = useState<View>('list');
  const [tickets, setTickets] = useState<SupportTicketSummary[]>([]);
  const [topics, setTopics] = useState<SupportTopicDto[]>([]);
  const [openTicket, setOpenTicket] = useState<SupportTicketDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  // Загрузка списка + тем при открытии.
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [t, tp] = await Promise.all([listSupportTickets(), getSupportTopics()]);
        setTickets(t);
        setTopics(tp);
      } catch (e: any) {
        toast(tr(`Не удалось загрузить: ${e?.message || 'ошибка'}`, `Couldn't load: ${e?.message || 'error'}`), 'red');
      } finally {
        setLoading(false);
      }
    })();
  }, [toast, tr]);

  const refreshTickets = async () => {
    try { setTickets(await listSupportTickets()); } catch { /* ignore */ }
  };

  const openChat = async (id: number) => {
    hapticTap();
    setLoading(true);
    try {
      const t = await getSupportTicket(id);
      setOpenTicket(t);
      setView('chat');
    } catch (e: any) {
      hapticError();
      toast(tr(`Не удалось открыть: ${e?.message}`, `Couldn't open: ${e?.message}`), 'red');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.backdrop} onClick={onClose}>
      <div style={styles.dialog} onClick={(e) => e.stopPropagation()}>
        <header style={styles.header}>
          {view !== 'list' && (
            <button style={styles.backBtn} onClick={() => {
              hapticTap();
              if (view === 'chat') { setOpenTicket(null); }
              setView('list');
              refreshTickets();
            }}>‹</button>
          )}
          <h3 style={styles.title}>
            {view === 'list'   && tr('ПОДДЕРЖКА', 'SUPPORT')}
            {view === 'create' && tr('НОВЫЙ ЗАПРОС', 'NEW REQUEST')}
            {view === 'chat'   && (openTicket?.code || tr('ТИКЕТ', 'TICKET'))}
          </h3>
          <button style={styles.closeBtn} onClick={onClose} aria-label={tr('Закрыть', 'Close')}>×</button>
        </header>

        <div style={styles.body}>
          {loading && <div style={styles.muted}>{tr('Загрузка…', 'Loading…')}</div>}

          {!loading && view === 'list' && (
            <TicketList
              tickets={tickets}
              STATUS_LABEL={STATUS_LABEL}
              onCreate={() => { hapticTap(); setView('create'); }}
              onOpen={openChat}
            />
          )}

          {!loading && view === 'create' && (
            <NewTicket
              topics={topics}
              busy={busy}
              onSubmit={async (topic, body) => {
                setBusy(true);
                try {
                  const r = await createSupportTicket({ topic, body });
                  hapticSuccess();
                  toast(tr(`Запрос создан (${r.code})`, `Request created (${r.code})`), 'green');
                  const t = await getSupportTicket(r.ticket_id);
                  setOpenTicket(t);
                  setView('chat');
                  await refreshTickets();
                } catch (e: any) {
                  hapticError();
                  if (String(e?.message).includes('rate_limit')) {
                    toast(tr('Слишком много запросов. Подожди час.', 'Too many requests. Wait an hour.'), 'red');
                  } else {
                    toast(tr(`Не удалось: ${e?.message}`, `Failed: ${e?.message}`), 'red');
                  }
                } finally {
                  setBusy(false);
                }
              }}
            />
          )}

          {!loading && view === 'chat' && openTicket && (
            <ChatView
              ticket={openTicket}
              busy={busy}
              STATUS_LABEL={STATUS_LABEL}
              onSend={async (body) => {
                if (!openTicket) return;
                setBusy(true);
                try {
                  await postSupportMessage(openTicket.id, body);
                  hapticSuccess();
                  const t = await getSupportTicket(openTicket.id);
                  setOpenTicket(t);
                } catch (e: any) {
                  hapticError();
                  if (String(e?.message).includes('ticket_closed')) {
                    toast(tr('Тикет закрыт. Создай новый.', 'Ticket closed. Create a new one.'), 'amber');
                  } else {
                    toast(tr(`Не удалось: ${e?.message}`, `Failed: ${e?.message}`), 'red');
                  }
                } finally {
                  setBusy(false);
                }
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// --- View 1: список тикетов ---
function TicketList({
  tickets, STATUS_LABEL, onCreate, onOpen
}: { tickets: SupportTicketSummary[]; STATUS_LABEL: Record<string, { txt: string; color: string }>; onCreate: () => void; onOpen: (id: number) => void }) {
  const tr = useTr();
  return (
    <div>
      <button style={styles.primaryBtn} onClick={onCreate}>
        + {tr('НОВЫЙ ЗАПРОС', 'NEW REQUEST')}
      </button>
      <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 6 }}>
        {tickets.length === 0 && (
          <div style={styles.muted}>{tr('Запросов пока нет.', 'No requests yet.')}</div>
        )}
        {tickets.map(t => {
          const s = STATUS_LABEL[t.status] || STATUS_LABEL.open;
          return (
            <button
              key={t.id}
              onClick={() => onOpen(t.id)}
              style={styles.ticketRow}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <span style={{ fontSize: 11, color: 'var(--t-md)', fontWeight: 600 }}>
                  {t.code}
                </span>
                <span style={{ fontSize: 10, color: s.color, fontWeight: 600 }}>
                  {s.txt}
                </span>
              </div>
              <div style={{ fontSize: 12, marginTop: 4, color: 'var(--t-hi)' }}>
                {topicLabel(t.topic, tr)} {t.subject && `· ${t.subject}`}
              </div>
              <div style={{ fontSize: 10, color: 'var(--t-lo)', marginTop: 2 }}>
                {fmtDate(t.last_response_at, tr)}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// --- View 2: создание тикета ---
function NewTicket({
  topics, busy, onSubmit
}: {
  topics: SupportTopicDto[];
  busy: boolean;
  onSubmit: (topic: SupportTopic, body: string) => Promise<void>;
}) {
  const tr = useTr();
  const [topic, setTopic] = useState<SupportTopic>('question');
  const [body, setBody] = useState('');
  const canSubmit = body.trim().length >= 2 && !busy;

  return (
    <div>
      <div style={{ fontSize: 11, color: 'var(--t-md)', letterSpacing: 1, marginBottom: 6 }}>{tr('ТЕМА', 'TOPIC')}</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
        {topics.map(t => (
          <button
            key={t.code}
            onClick={() => setTopic(t.code as SupportTopic)}
            style={{
              ...styles.topicBtn,
              ...(topic === t.code ? styles.topicBtnActive : {})
            }}
          >{topicLabel(t.code, tr)}</button>
        ))}
      </div>
      <div style={{ fontSize: 11, color: 'var(--t-md)', letterSpacing: 1, marginBottom: 6 }}>{tr('СООБЩЕНИЕ', 'MESSAGE')}</div>
      <textarea
        value={body}
        onChange={e => setBody(e.target.value)}
        placeholder={tr('Опиши проблему подробно…', 'Describe the problem in detail…')}
        rows={6}
        maxLength={4096}
        style={styles.textarea}
      />
      <div style={{ fontSize: 10, color: 'var(--t-lo)', textAlign: 'right', marginBottom: 10 }}>
        {body.length} / 4096
      </div>
      <button
        style={{
          ...styles.primaryBtn,
          opacity: canSubmit ? 1 : 0.5,
          cursor: canSubmit ? 'pointer' : 'not-allowed'
        }}
        disabled={!canSubmit}
        onClick={() => onSubmit(topic, body.trim())}
      >{tr('ОТПРАВИТЬ', 'SEND')}</button>
    </div>
  );
}

// --- View 3: чат с саппортом ---
function ChatView({
  ticket, busy, STATUS_LABEL, onSend
}: {
  ticket: SupportTicketDetail;
  busy: boolean;
  STATUS_LABEL: Record<string, { txt: string; color: string }>;
  onSend: (body: string) => Promise<void>;
}) {
  const tr = useTr();
  const [text, setText] = useState('');
  const isClosed = ticket.status === 'closed';
  const scrollRef = useRef<HTMLDivElement>(null);
  const status = STATUS_LABEL[ticket.status] || STATUS_LABEL.open;

  // Скролл вниз при открытии и после нового сообщения.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [ticket.messages.length]);

  const canSend = text.trim().length >= 1 && !busy && !isClosed;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{
        padding: '6px 10px',
        background: 'rgba(255,255,255,0.04)',
        fontSize: 11,
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: 10
      }}>
        <span style={{ color: 'var(--t-md)' }}>
          {topicLabel(ticket.topic, tr)}
        </span>
        <span style={{ color: status.color, fontWeight: 600 }}>{status.txt}</span>
      </div>
      <div
        ref={scrollRef}
        style={{
          flex: 1, minHeight: 200, maxHeight: '50vh', overflowY: 'auto',
          display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10,
          padding: '4px 2px'
        }}
      >
        {ticket.messages.map(m => (
          <div
            key={m.id}
            style={{
              alignSelf: m.sender === 'user' ? 'flex-end' : 'flex-start',
              maxWidth: '80%',
              padding: '8px 10px',
              background: m.sender === 'user' ? 'rgba(61,240,255,0.10)' : 'rgba(255,177,61,0.10)',
              border: `0.5px solid ${m.sender === 'user' ? 'var(--neon-cy)' : 'var(--neon-am)'}`,
              fontSize: 13,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word'
            }}
          >
            <div style={{
              fontSize: 9,
              color: 'var(--t-lo)',
              marginBottom: 4,
              letterSpacing: 0.5
            }}>
              {m.sender === 'user' ? tr('ТЫ', 'YOU') : tr('САППОРТ', 'SUPPORT')} · {fmtDate(m.created_at, tr)}
            </div>
            {m.body}
          </div>
        ))}
      </div>
      {isClosed ? (
        <div style={{ fontSize: 11, color: 'var(--t-md)', textAlign: 'center', padding: 10 }}>
          {tr(
            'Тикет закрыт. Если ещё нужна помощь — создай новый.',
            'Ticket closed. If you still need help — create a new one.'
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 6 }}>
          <input
            type="text"
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && canSend) {
                onSend(text.trim()).then(() => setText(''));
              }
            }}
            placeholder={tr('Сообщение…', 'Message…')}
            disabled={busy}
            style={styles.chatInput}
          />
          <button
            disabled={!canSend}
            style={{
              ...styles.primaryBtn,
              flex: 0,
              padding: '0 14px',
              opacity: canSend ? 1 : 0.5,
              cursor: canSend ? 'pointer' : 'not-allowed'
            }}
            onClick={() => onSend(text.trim()).then(() => setText(''))}
          >→</button>
        </div>
      )}
    </div>
  );
}

const TOPIC_LABEL_RU: Record<string, string> = {
  bug: 'Баг', payment: 'Платёж', question: 'Вопрос',
  feedback: 'Фидбэк', other: 'Прочее'
};
const TOPIC_LABEL_EN: Record<string, string> = {
  bug: 'Bug', payment: 'Payment', question: 'Question',
  feedback: 'Feedback', other: 'Other'
};
function topicLabel(t: string, tr: (ru: string, en: string) => string) {
  const isEn = tr('ru', 'en') === 'en';
  const map = isEn ? TOPIC_LABEL_EN : TOPIC_LABEL_RU;
  return map[t] || t;
}

function fmtDate(iso: string, tr: (ru: string, en: string) => string) {
  const d = new Date(iso);
  const now = Date.now();
  const diff = now - d.getTime();
  const isEn = tr('ru', 'en') === 'en';
  if (diff < 60_000) return tr('только что', 'just now');
  if (diff < 3_600_000) return Math.floor(diff / 60_000) + (isEn ? ' min' : ' мин');
  if (diff < 86_400_000) return Math.floor(diff / 3_600_000) + (isEn ? ' h' : ' ч');
  return d.toLocaleDateString(isEn ? 'en-US' : 'ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

const styles: Record<string, React.CSSProperties> = {
  backdrop: {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.7)',
    zIndex: 200,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 12
  },
  dialog: {
    background: 'var(--bg-2, #101018)',
    width: '100%', maxWidth: 480,
    maxHeight: '90vh',
    boxShadow: 'inset 0 0 0 0.5px rgba(160,170,200,0.18)',
    display: 'flex', flexDirection: 'column'
  },
  header: {
    display: 'flex', alignItems: 'center',
    padding: '10px 14px',
    borderBottom: '0.5px solid rgba(160,170,200,0.18)',
    gap: 8
  },
  title: {
    flex: 1, fontSize: 13, letterSpacing: '0.12em',
    color: 'var(--neon-cy)', fontWeight: 600
  },
  backBtn: {
    background: 'transparent', border: 'none',
    color: 'var(--t-hi)', fontSize: 22,
    cursor: 'pointer', padding: 0, width: 20
  },
  closeBtn: {
    background: 'transparent', border: 'none',
    color: 'var(--t-md)', fontSize: 24,
    cursor: 'pointer', padding: 0, width: 24
  },
  body: {
    padding: 14, overflowY: 'auto', flex: 1,
    display: 'flex', flexDirection: 'column'
  },
  muted: { color: 'var(--t-md)', fontSize: 12, textAlign: 'center', padding: 20 },
  primaryBtn: {
    width: '100%', padding: '10px',
    background: 'var(--neon-cy)', color: 'var(--bg, #07070B)',
    border: 'none', fontSize: 12, fontWeight: 700,
    letterSpacing: 1, cursor: 'pointer', fontFamily: 'inherit'
  },
  ticketRow: {
    padding: '10px 12px',
    background: 'rgba(255,255,255,0.03)',
    border: '0.5px solid rgba(160,170,200,0.18)',
    color: 'var(--t-hi)', textAlign: 'left',
    cursor: 'pointer', fontFamily: 'inherit',
    width: '100%'
  },
  topicBtn: {
    padding: '6px 12px', fontSize: 11,
    background: 'transparent', color: 'var(--t-md)',
    border: '0.5px solid var(--t-md)',
    cursor: 'pointer', fontFamily: 'inherit'
  },
  topicBtnActive: {
    background: 'var(--neon-cy)', color: 'var(--bg, #07070B)',
    borderColor: 'var(--neon-cy)', fontWeight: 700
  },
  textarea: {
    width: '100%', padding: 10,
    background: 'var(--bg, #07070B)',
    color: 'var(--t-hi)',
    border: '0.5px solid rgba(160,170,200,0.18)',
    fontSize: 13, fontFamily: 'inherit',
    resize: 'vertical'
  },
  chatInput: {
    flex: 1, padding: '10px 12px',
    background: 'var(--bg, #07070B)',
    color: 'var(--t-hi)',
    border: '0.5px solid rgba(160,170,200,0.18)',
    fontSize: 13, fontFamily: 'inherit'
  }
};

// Suppress unused imports if any flow doesn't use them.
void useMemo;
